/**
 * @file    useChartColors.ts
 * @scope   SHARED
 * @summary Owns every colour concern of the drawer: the colour/palette pickers, the temporary
 *          buffers they write to, and the propagation of those buffers to the edited widget.
 * @consumes Global store (`customWidget`, `widgetsList`, `setState`), colour capability tables
 * @produces Picker setters, initial values for the inputs and `colorByChartType` for widget rebuilds
 * @sideEffects Writes `customWidget.colors` to the global store whenever a buffer changes.
 * @notes LEGACY-ONLY in practice: Enterprise V2 widgets do not expose colour pickers, but the hook
 *        is kept in `common/` because the buffers also feed the generic widget-rebuild flow.
 */
import { useEffect, useState } from 'react';
import {
  colorFamilyInitialState,
  colorInitialState,
  ColorInputValue,
  ThemeInputValue,
} from 'constants/colorsInitialStates';
import { chartsWithColor, chartsWithColorFamilies } from '../constants/chartCapabilities';

/** Everything the drawer needs from the colour domain. */
export type UseChartColorsResult = {
  /** Current value of the single-colour picker. */
  selectedColor: ColorInputValue;
  /** Setter handed to `ColorInput`. */
  setSelectedColor: React.Dispatch<React.SetStateAction<ColorInputValue>>;
  /** Current value of the palette picker. */
  selectedColorFamily: ThemeInputValue;
  /** Setter handed to `ColorFamilyInput`. */
  setSelectedColorFamily: React.Dispatch<React.SetStateAction<ThemeInputValue>>;
  /** Resolves the colour shown by the single-colour picker at `index`. */
  initColor: (index: number) => string;
  /** Resolves the palette shown by the palette picker. */
  initColorFamily: () => string[];
  /** Resolves the colours to assign when the widget is rebuilt after a chart-type switch. */
  colorByChartType: (alias: string) => string[];
};

/**
 * @param customWidget Widget currently being edited (source of the persisted colours).
 * @param widgetsList  Widget catalogue, used to fall back to a chart's default palette.
 * @param setState     Global store setter.
 */
export const useChartColors = (
  customWidget: any,
  widgetsList: any[],
  setState: (partial: any) => void
): UseChartColorsResult => {
  const [selectedColor, setSelectedColor] = useState<ColorInputValue>(colorInitialState);
  const [selectedColorFamily, setSelectedColorFamily] =
    useState<ThemeInputValue>(colorFamilyInitialState);

  // WHY: the pickers write to these buffers instead of the store directly, so a colour change is
  // committed in a second effect. Keeping the two-step flow preserves the original render timing.
  const [tempColor, setTempColor] = useState<string[]>([]);
  const [tempFamilyColor, setTempFamilyColor] = useState<string[]>([]);

  // Single-colour picker -> buffer. The picker reports an index so multi-series charts can grow
  // the array one slot at a time.
  useEffect(() => {
    if (selectedColor) {
      const colors = [...customWidget.colors];
      if (colors[selectedColor.index]) {
        colors[selectedColor.index] = selectedColor.color;
      } else {
        colors?.push(selectedColor.color);
      }
      setTempColor(colors);
    }
  }, [selectedColor]);

  // Palette picker -> buffer.
  useEffect(() => {
    if (selectedColorFamily) {
      setTempFamilyColor(selectedColorFamily.theme);
    }
  }, [selectedColorFamily]);

  // Buffer -> store, but only for charts that actually support the corresponding picker.
  useEffect(() => {
    if (chartsWithColor.includes(customWidget.alias))
      setState({ customWidget: { ...customWidget, colors: tempColor } });
  }, [tempColor]);

  useEffect(() => {
    if (chartsWithColorFamilies.includes(customWidget.alias))
      setState({ customWidget: { ...customWidget, colors: tempFamilyColor } });
  }, [tempFamilyColor]);

  /** Buffer wins over the persisted value so the picker reflects unsaved edits. */
  const initColor = (index: number): string => {
    if (tempColor?.length > 0) return tempColor[index];
    if (customWidget.colors) return customWidget.colors[index];
    return colorInitialState.color;
  };

  /** Buffer wins over the persisted value so the picker reflects unsaved edits. */
  const initColorFamily = (): string[] => {
    if (tempFamilyColor?.length > 0) return tempFamilyColor;
    if (customWidget.colors) return customWidget.colors;
    return colorFamilyInitialState.theme;
  };

  /**
   * Colours to apply when the widget is rebuilt after a chart-type switch:
   * keep the user's buffer when the new chart supports a picker, otherwise fall back to the
   * chart's own defaults from the widget catalogue.
   */
  const colorByChartType = (alias: string): string[] => {
    if (chartsWithColor.includes(alias)) return tempColor;
    if (chartsWithColorFamilies.includes(alias)) return tempFamilyColor;

    const widget = widgetsList.find((w) => w.component === alias);
    return widget?.defaults.colors ? widget.defaults.colors : [];
  };

  return {
    selectedColor,
    setSelectedColor,
    selectedColorFamily,
    setSelectedColorFamily,
    initColor,
    initColorFamily,
    colorByChartType,
  };
};
